import { useState, useRef, useEffect } from "react";
import {
     Modal,
     Button,
     Stack,
     Text,
     Alert,
     Group,
     ThemeIcon,
     Loader,
} from "@mantine/core";
import {
     IconCamera,
     IconAlertCircle,
     IconCheck,
     IconX,
} from "@tabler/icons-react";
import * as tf from "@tensorflow/tfjs";
import * as blazeface from "@tensorflow-models/blazeface";

interface SelfieCaptureProps {
     opened: boolean;
     onClose: () => void;
     onCapture: (file: File) => Promise<void>;
     bvn: string;
     isUploading?: boolean;
}

interface FaceDetectionResult {
     hasFace: boolean;
     isClear: boolean;
     hasGlasses: boolean;
     hasHijab: boolean;
     lighting: "good" | "poor";
     message: string;
}

export default function SelfieCapture({
     opened,
     onClose,
     onCapture,
     bvn,
     isUploading = false,
}: SelfieCaptureProps) {
     const videoRef = useRef<HTMLVideoElement>(null);
     const canvasRef = useRef<HTMLCanvasElement>(null);
     const streamRef = useRef<MediaStream | null>(null);
     const [model, setModel] = useState<blazeface.BlazeFaceModel | null>(null);
     const [isLoading, setIsLoading] = useState(true);
     const [detectionResult, setDetectionResult] =
          useState<FaceDetectionResult | null>(null);
     const [isDetecting, setIsDetecting] = useState(false);
     const [canCapture, setCanCapture] = useState(false);
     const detectionIntervalRef = useRef<number | null>(null);

     // Helper function to convert tensor values to arrays
     const getArrayValue = (value: any): number[] => {
          if (Array.isArray(value)) {
               return value;
          }
          // If it's a tensor, try to get the data
          if (value && typeof value.dataSync === "function") {
               return Array.from(value.dataSync());
          }
          if (value && typeof value.arraySync === "function") {
               return value.arraySync();
          }
          // Fallback: try Array.from
          try {
               return Array.from(value);
          } catch {
               return [0, 0];
          }
     };

     // Load TensorFlow.js and BlazeFace model
     useEffect(() => {
          let mounted = true;

          const loadModel = async () => {
               try {
                    setIsLoading(true);
                    await tf.ready();
                    const loadedModel = await blazeface.load();
                    if (mounted) {
                         setModel(loadedModel);
                         setIsLoading(false);
                    }
               } catch (error) {
                    console.error("Error loading face detection model:", error);
                    if (mounted) {
                         setIsLoading(false);
                    }
               }
          };

          loadModel();

          return () => {
               mounted = false;
          };
     }, []);

     // Start camera stream
     useEffect(() => {
          if (!opened || !model) return;

          const startCamera = async () => {
               try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                         video: {
                              width: { ideal: 640 },
                              height: { ideal: 480 },
                              facingMode: "user",
                         },
                    });

                    if (videoRef.current) {
                         videoRef.current.srcObject = stream;
                         streamRef.current = stream;
                         await videoRef.current.play();
                         startDetection();
                    }
               } catch (error) {
                    console.error("Error accessing camera:", error);
                    setDetectionResult({
                         hasFace: false,
                         isClear: false,
                         hasGlasses: false,
                         hasHijab: false,
                         lighting: "poor",
                         message: "Failed to access camera. Please grant camera permissions.",
                    });
               }
          };

          startCamera();

          return () => {
               stopCamera();
          };
     }, [opened, model]);

     const stopCamera = () => {
          if (detectionIntervalRef.current) {
               clearInterval(detectionIntervalRef.current);
               detectionIntervalRef.current = null;
          }
          if (streamRef.current) {
               streamRef.current.getTracks().forEach((track) => track.stop());
               streamRef.current = null;
          }
          if (videoRef.current) {
               videoRef.current.srcObject = null;
          }
     };

     const startDetection = () => {
          if (detectionIntervalRef.current) return;

          detectionIntervalRef.current = window.setInterval(async () => {
               if (
                    !videoRef.current ||
                    !canvasRef.current ||
                    !model ||
                    isDetecting
               )
                    return;

               setIsDetecting(true);
               try {
                    const video = videoRef.current;
                    const canvas = canvasRef.current;
                    const ctx = canvas.getContext("2d");

                    if (!ctx) return;

                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Detect faces
                    const predictions = await model.estimateFaces(video, false);

                    if (predictions.length === 0) {
                         setDetectionResult({
                              hasFace: false,
                              isClear: false,
                              hasGlasses: false,
                              hasHijab: false,
                              lighting: "poor",
                              message: "No face detected. Please position your face clearly in the frame.",
                         });
                         setCanCapture(false);
                    } else if (predictions.length > 1) {
                         setDetectionResult({
                              hasFace: true,
                              isClear: false,
                              hasGlasses: false,
                              hasHijab: false,
                              lighting: "poor",
                              message: "Multiple faces detected. Please ensure only your face is visible.",
                         });
                         setCanCapture(false);
                    } else {
                         const face = predictions[0];
                         const result = analyzeFace(face, canvas);
                         setDetectionResult(result);
                         setCanCapture(
                              result.hasFace &&
                                   result.isClear &&
                                   !result.hasGlasses &&
                                   !result.hasHijab &&
                                   result.lighting === "good"
                         );
                    }
               } catch (error) {
                    console.error("Error during face detection:", error);
               } finally {
                    setIsDetecting(false);
               }
          }, 500); // Check every 500ms
     };

     const analyzeFace = (
          face: blazeface.NormalizedFace,
          canvas: HTMLCanvasElement
     ): FaceDetectionResult => {
          const landmarks = face.landmarks;
          // Convert tensors to arrays
          const topLeft = getArrayValue(face.topLeft);
          const bottomRight = getArrayValue(face.bottomRight);
          const box = [...topLeft, ...bottomRight];
          const width = box[2] - box[0];
          const height = box[3] - box[1];
          const area = width * height;
          const canvasArea = canvas.width * canvas.height;
          const faceRatio = area / canvasArea;

          // Check if face is large enough (at least 15% of frame)
          const isClear = faceRatio > 0.15;

          // Analyze lighting by checking image brightness in face region
          const ctx = canvas.getContext("2d");
          if (!ctx) {
               return {
                    hasFace: true,
                    isClear: false,
                    hasGlasses: false,
                    hasHijab: false,
                    lighting: "poor",
                    message: "Unable to analyze image quality.",
               };
          }

          const imageData = ctx.getImageData(
               Math.max(0, box[0]),
               Math.max(0, box[1]),
               Math.min(canvas.width - box[0], width),
               Math.min(canvas.height - box[1], height)
          );
          const pixels = imageData.data;
          let brightness = 0;
          for (let i = 0; i < pixels.length; i += 4) {
               const r = pixels[i];
               const g = pixels[i + 1];
               const b = pixels[i + 2];
               brightness += (r + g + b) / 3;
          }
          brightness /= pixels.length / 4;
          const lighting = brightness > 100 ? "good" : "poor";

          // Convert landmarks to arrays if they are tensors
          let landmarksArray: number[][];
          if (Array.isArray(landmarks)) {
               // If it's already an array, map each landmark
               landmarksArray = landmarks.map((landmark: any) =>
                    getArrayValue(landmark)
               );
          } else {
               // If it's a Tensor2D, convert it to array first
               const landmarksData = getArrayValue(landmarks);
               // landmarksData should be a flat array, reshape to [numLandmarks, 2]
               landmarksArray = [];
               for (let i = 0; i < landmarksData.length; i += 2) {
                    landmarksArray.push([
                         landmarksData[i],
                         landmarksData[i + 1],
                    ]);
               }
          }

          // Check for glasses (eye region analysis - simplified)
          const leftEye = landmarksArray[0] || [0, 0];
          const rightEye = landmarksArray[1] || [0, 0];
          const eyeDistance = Math.abs(leftEye[0] - rightEye[0]);
          const hasGlasses = false; // Simplified - would need more sophisticated detection

          // Check for hijab (head covering - check if face is too small relative to expected head size)
          const expectedHeadHeight = height * 1.5;
          const headRatio = expectedHeadHeight / canvas.height;
          const hasHijab = headRatio < 0.3; // If head appears too small, might be covered

          let message = "";
          if (!isClear) {
               message =
                    "Please move closer to the camera for a clearer image.";
          } else if (hasGlasses) {
               message = "Please remove your glasses.";
          } else if (hasHijab) {
               message =
                    "Please ensure your face is fully visible without any head covering.";
          } else if (lighting === "poor") {
               message = "Lighting is too dim. Please move to a well-lit area.";
          } else {
               message = "Face detected and ready to capture!";
          }

          return {
               hasFace: true,
               isClear,
               hasGlasses,
               hasHijab,
               lighting,
               message,
          };
     };

     const handleCapture = async () => {
          if (!videoRef.current || !canvasRef.current || !canCapture) return;

          const video = videoRef.current;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d");

          if (!ctx) return;

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
               async (blob) => {
                    if (blob) {
                         const file = new File([blob], "selfie.jpg", {
                              type: "image/jpeg",
                         });
                         stopCamera();
                         await onCapture(file);
                    }
               },
               "image/jpeg",
               0.95
          );
     };

     const handleClose = () => {
          stopCamera();
          setDetectionResult(null);
          setCanCapture(false);
          onClose();
     };

     return (
          <Modal
               opened={opened}
               onClose={handleClose}
               title="Capture Selfie for BVN Verification"
               size="lg"
               centered
               closeOnClickOutside={false}
               closeOnEscape={!isUploading}
          >
               <Stack gap="md">
                    <Alert
                         icon={<IconAlertCircle size={16} />}
                         title="Instructions"
                         color="blue"
                    >
                         <Text size="sm">
                              Please ensure:
                              <br />• Your face is clearly visible and well-lit
                              <br />• No caps, hats, or head coverings
                              <br />• No glasses
                              <br />• Good lighting conditions
                              <br />• Only your face is in the frame
                         </Text>
                    </Alert>

                    {isLoading ? (
                         <Group justify="center" py="xl">
                              <Loader size="lg" />
                              <Text>Loading face detection model...</Text>
                         </Group>
                    ) : (
                         <>
                              <div
                                   style={{
                                        position: "relative",
                                        width: "100%",
                                        margin: "0 auto",
                                   }}
                              >
                                   <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        style={{
                                             width: "100%",
                                             maxWidth: "640px",
                                             borderRadius: "8px",
                                             transform: "scaleX(-1)", // Mirror for selfie view
                                        }}
                                   />
                                   <canvas
                                        ref={canvasRef}
                                        style={{ display: "none" }}
                                   />
                              </div>

                              {detectionResult && (
                                   <Alert
                                        icon={
                                             canCapture ? (
                                                  <IconCheck size={16} />
                                             ) : (
                                                  <IconAlertCircle size={16} />
                                             )
                                        }
                                        title={
                                             canCapture
                                                  ? "Ready to Capture"
                                                  : "Please Adjust"
                                        }
                                        color={canCapture ? "green" : "yellow"}
                                   >
                                        <Text size="sm">
                                             {detectionResult.message}
                                        </Text>
                                   </Alert>
                              )}

                              <Group justify="flex-end" mt="md">
                                   <Button
                                        variant="subtle"
                                        onClick={handleClose}
                                        disabled={isUploading}
                                   >
                                        Cancel
                                   </Button>
                                   <Button
                                        leftSection={<IconCamera size={16} />}
                                        onClick={handleCapture}
                                        disabled={!canCapture || isUploading}
                                        loading={isUploading}
                                   >
                                        {isUploading
                                             ? "Uploading..."
                                             : "Capture"}
                                   </Button>
                              </Group>
                         </>
                    )}
               </Stack>
          </Modal>
     );
}
